import {createContext, useContext, useState, useEffect} from 'react';

import appConstants from '../../../../../constants';

import useFetchFilters from '../../../../../hooks/data/filters/useFetchFilters';

import useCountryFilter from '../../../../../hooks/data/filters/useCountryFilter';

import useUserGeoLocation from '../../../../../hooks/data/user/useUserGeoLocation';

import useSearchFormTabNavigation from '../../../../../hooks/ui/useSearchFormTabNavigation';

import usePartnerRoleFilter from '../../../../../hooks/data/filters/usePartnerRoleFilter';
import usePartnerLevelFilter from '../../../../../hooks/data/filters/usePartnerLevelFilter';
import usePartnerExpertiseFilter from '../../../../../hooks/data/filters/usePartnerExpertiseFilter';

import useDebounce from '../../../../../hooks/ui/useDebounce';
import useGetSuggestions from '../../../../../hooks/data/suggestions/useGetSuggestions';
import useSuggestions from '../../../../../hooks/data/suggestions/useSuggestions';

import useSearchCriteria from '../../../../../hooks/data/searchCriteria/useSearchCriteria';
import useOnCountrySelectionChange from "../../../../../hooks/data/filters/onCountrySelectionChange";
import useRecentSearches from '../../../../../hooks/data/search/useRecentSearches';

import usePartnerSearch from '../../../../../hooks/data/search/usePartnerSearch';

import useSortingParameter from '../../../../../hooks/data/sorting/useSortingParameter';
import useSearchRestoration from '../../../../../hooks/data/search/useSearchRestoration';
import usePagination from '../../../../../hooks/data/usePagination';
import useComparator from '../../../../../hooks/data/useComparator';

import useMobileFilterToggle from '../../../../../hooks/ui/useMobileFilterToggle';
import getLocalStorageItem from '../../../../../utils/getLocalStorageItem';
import setLocalStorageItem from '../../../../../utils/setLocalStorageItem';

import useSearchParams from '../../../../../hooks/device/useSearchParams';
import {useLocation} from "react-router-dom";

const PartnerSearchContext = createContext();

export function usePartnerSearchContext() {
  const context = useContext(PartnerSearchContext);
  if (!context) {
    console.error('usePartnerSearchContext: the component from which you are trying to use this context must be a ContextProviderPartnerSearch component descendant.');
  }
  return context;
}

export default function ContextProviderPartnerSearch({children}) {
  // Load filter data.
  const {
    isFilterLoadingInProgress,
    filterLoadingError,
    countryList,
    partnerRoleList,
    partnerIntegratorLevelList,
    partnerIntegratorSpecializationList,
    partnerProviderLevelList,
    partnerProviderCiscoPoweredServiceList,
    partnerDeveloperLevelList,
    partnerDeveloperSpecializationList,
    partnerAdvisorLevelList,
    partnerAdvisorSpecializationList,
    areFiltersLoaded
  } = useFetchFilters();

  const searchParams = useSearchParams();
  const { location } = useLocation();

  // Intercepting query string parameters.
  // Locale is not used yet.
  const incomingLinkParamLocale = searchParams.get('locale');
  const incomingLinkParamLocation = searchParams.get('location');
  const incomingLinkParamRole = searchParams.get('role');
  const incomingLinkParamExpertise = searchParams.get('expertise');

  // Fetching possible stored default/initial filter values.
  const [isLoggedInUser, setIsLoggedInUser] = useState(getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_LOGGED_IN_USER, false));
  //const isLoggedInUser = getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_LOGGED_IN_USER,false);
  const [initialSelectedCountry, setInitialSelectedCountry] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_COUNTRY, null) : null);
  const [initialSelectedPartnerRole, setInitialSelectedPartnerRole] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_ROLE, null) : null);
  const [initialSelectedPartnerLevels, setInitialSelectedPartnerLevels] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_LEVELS, []) : []);
  const [initialSelectedPartnerExpertises, setInitialSelectedPartnerExpertises] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_EXPERTISES, []) : []);
  const [initialSelectedPartnerSearchForm, setInitialSelectedPartnerSearchForm] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_SEARCH_FORM, appConstants.SEARCH_FORM_DEFAULT) : appConstants.SEARCH_FORM_DEFAULT);
  const [initialLastSelectedPartnerSearchForm, setInitialLastSelectedPartnerSearchForm] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_LAST_SELECTED_PARTNER_SEARCH_FORM, appConstants.SEARCH_FORM_FILTER_BY_NEED) : appConstants.SEARCH_FORM_FILTER_BY_NEED);
  const [initialSelectedPartnerSiteId, setInitialSelectedPartnerSiteId] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_SITE_ID, null) : null);

  const [initialCurrentPageIndex, setInitialCurrentPageIndex] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_CURRENT_PAGE_INDEX, 1) : 1);
  const [initialSelectedSortingParameterValue, setInitialSelectedSortingParameterValue] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SORTING_PARAMETER, appConstants.SORTING_PARAMETER_DEFAULT) : appConstants.SORTING_PARAMETER_DEFAULT);

  const [initialSelectedPartnerIntegratorLevelSuggestions, setInitialSelectedPartnerIntegratorLevelSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_INTEGRATOR_LEVELS, []) : []);
  const [initialSelectedPartnerProviderLevelSuggestions, setInitialSelectedPartnerProviderLevelSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_PROVIDER_LEVELS, []) : []);
  const [initialSelectedPartnerDeveloperLevelSuggestions, setInitialSelectedPartnerDeveloperLevelSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_DEVELOPER_LEVELS, []) : []);
  const [initialSelectedPartnerAdvisorLevelSuggestions, setInitialSelectedPartnerAdvisorLevelSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_ADVISOR_LEVELS, []) : []);
  const [initialSelectedPartnerIntegratorSpecializationSuggestions, setInitialSelectedPartnerIntegratorSpecializationSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_INTEGRATOR_SPECIALIZATIONS, []) : []);
  const [initialSelectedPartnerProviderCiscoPoweredServiceSuggestions, setInitialSelectedPartnerProviderCiscoPoweredServiceSuggestions] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_PROVIDER_CPS, []) : []);
  const [initialSelectedPartnerNameSuggestion, setInitialSelectedPartnerNameSuggestion] = useState(!!isLoggedInUser ? getLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_NAME, null) : null);

  // Set country filter values & selection methods.
  const {
    isCountryFilterDropdownOpened,
    countryFilterValues,
    //previouslySelectedCountry,
    previouslySelectedCountryCode,
    selectedCountry,
    selectedCountryCode,
    selectedCountryName,
    selectCountryFilterValue,
    unselectCountryFilterValue,
    resetCountryFilterValues,
    syncPreviousAndCurrentlySelectedCountry,
    toggleCountryFilterDropdown,
    isIncomingLinkParamLocationSuccessfullySelected
  } = useCountryFilter(countryList, initialSelectedCountry, incomingLinkParamLocation);

  // Geo localize (or try to) the user.
  const {
    isUserGeoLocationInProgress,
    isUserGeoLocationCompleted
  } = useUserGeoLocation(areFiltersLoaded, filterLoadingError, countryList, selectCountryFilterValue, initialSelectedCountry !== null || isIncomingLinkParamLocationSuccessfullySelected);


  // Define user search/filter form navigation.
  const {
    getCurrentSelectedForm,
    selectFilterByNeedForm,
    selectSearchByCombinedCriteriaForm,
    selectSearchByLocationForm,
    selectFormBeforeSearchByLocation,
    isFilterByNeedFormSelected,
    isSearchByCombinedCriteriaFormSelected,
    isSearchByLocationFormSelected,
    lastSelectedNonSearchByLocationForm
  } = useSearchFormTabNavigation(selectedCountry, initialSelectedPartnerSearchForm, initialLastSelectedPartnerSearchForm);

  // Set partner role filter data & selection methods.
  const {
    partnerRoleFilterValues,
    isPartnerRoleFilterDropdownOpened,
    selectedPartnerRole,
    selectedPartnerRoleCode,
    selectPartnerRoleFilterValue,
    unselectPartnerRoleFilterValue,
    resetPartnerRoleFilterValues,
    //getSelectedCopyOfPartnerRoleFilterValue,
    togglePartnerRoleFilterDropdown
  } = usePartnerRoleFilter(partnerRoleList, initialSelectedPartnerRole, incomingLinkParamRole);

  // Set partner level filter data & selection methods.
  const {
    isPartnerLevelFilterDropdownOpened,
    partnerLevelFilterValues,
    togglePartnerLevelFilterValue,
    resetPartnerLevelFilterValues,
    togglePartnerLevelFilterDropdown,
    selectedPartnerLevels
  } = usePartnerLevelFilter(selectedPartnerRoleCode, partnerIntegratorLevelList, partnerProviderLevelList, partnerDeveloperLevelList, partnerAdvisorLevelList, resetPartnerRoleFilterValues, initialSelectedPartnerLevels);

  // Set partner expertise (specialization & cisco powered services) filter data & selection methods.
  const {
    isPartnerExpertiseFilterDropdownOpened,
    partnerExpertiseFilterValues,
    togglePartnerExpertiseFilterValue,
    resetPartnerExpertiseFilterValues,
    togglePartnerExpertiseFilterDropdown,
    selectedPartnerExpertises,
  } = usePartnerExpertiseFilter(selectedPartnerRoleCode, partnerIntegratorSpecializationList, partnerProviderCiscoPoweredServiceList, partnerDeveloperSpecializationList, partnerAdvisorSpecializationList, initialSelectedPartnerExpertises, incomingLinkParamExpertise);

  // Create selection/unselection mapping between filter value TYPE-SUBTYPE and selection/unselection methods.
  //const filterTypeSubTypeSelectFnMapping = {
  //  [`${appConstants.VALUE_LOCATION}-${appConstants.VALUE_COUNTRY}`]: selectCountryFilterValue,
  //  [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_INTEGRATOR}`]: selectPartnerRoleFilterValue,
  //  [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_PROVIDER}`]: selectPartnerRoleFilterValue,
  //  [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_INTEGRATOR}`]: togglePartnerLevelFilterValue,
  //  [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_PROVIDER}`]: togglePartnerLevelFilterValue,
  //  [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_INTEGRATOR}`]: togglePartnerExpertiseFilterValue,
  //  [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_PROVIDER}`]: togglePartnerExpertiseFilterValue,
  //};

  const filterTypeSubTypeUnselectFnMapping ={
    [`${appConstants.VALUE_LOCATION}-${appConstants.VALUE_COUNTRY}`]: unselectCountryFilterValue,
    [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_INTEGRATOR}`]: unselectPartnerRoleFilterValue,
    [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_PROVIDER}`]: unselectPartnerRoleFilterValue,
    [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_DEVELOPER}`]: unselectPartnerRoleFilterValue,
    [`${appConstants.VALUE_PARTNER_ROLE}-${appConstants.VALUE_ADVISOR}`]: unselectPartnerRoleFilterValue,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_INTEGRATOR}`]: togglePartnerLevelFilterValue,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_PROVIDER}`]: togglePartnerLevelFilterValue,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_DEVELOPER}`]: togglePartnerLevelFilterValue,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_ADVISOR}`]: togglePartnerLevelFilterValue,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_INTEGRATOR}`]: togglePartnerExpertiseFilterValue,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_PROVIDER}`]: togglePartnerExpertiseFilterValue,
  };

  // Define smart search, searched terms.
  const [searchTerms, setSearchTerms] = useState(()=>'');
  function updateSearchTerms(st) {
    const correctedSearchTerms = !st ? '':st;
    setSearchTerms(searchTerms => correctedSearchTerms);
    if (!correctedSearchTerms.length) {
      resetSuggestions();
    }
  }
  function clearSearchTerms() {
    setSearchTerms(searchTerms => '');
    resetSuggestions();
  }
  const debouncedSearchTerms = useDebounce(searchTerms, appConstants.SEARCH_TERMS_GET_SUGGESTION_DEBOUNCE_DELAY_MILLISECONDS);

  //
  const {
    isGetSuggestionsProcessingInProgress,
    getSuggestionsProcessingError,
    partnerIntegratorLevelSuggestions,
    partnerProviderLevelSuggestions,
    partnerDeveloperLevelSuggestions,
    partnerAdvisorLevelSuggestions,
    partnerIntegratorSpecializationSuggestions,
    partnerProviderCiscoPoweredServiceSuggestions,
    partnerNameSuggestions,
    searchedTermsAsPartnerNameSuggestion,
    resetSuggestions
  } = useGetSuggestions(debouncedSearchTerms, selectedCountry)

  const {
    selectedPartnerIntegratorLevelSuggestions,
    selectedPartnerProviderLevelSuggestions,
    selectedPartnerDeveloperLevelSuggestions,
    selectedPartnerAdvisorLevelSuggestions,
    selectedPartnerIntegratorSpecializationSuggestions,
    selectedPartnerProviderCiscoPoweredServiceSuggestions,
    selectedPartnerNameSuggestion,
    selectedSearchTermsAsPartnerName,

    selectPartnerIntegratorLevelSuggestion,
    selectPartnerProviderLevelSuggestion,
    selectPartnerDeveloperLevelSuggestion,
    selectPartnerAdvisorLevelSuggestion,
    selectPartnerIntegratorSpecializationSuggestion,
    selectPartnerProviderCiscoPoweredServiceSuggestion,
    selectPartnerNameSuggestion,
    selectSearchedTermsAsPartnerName,

    unselectPartnerIntegratorLevelSuggestion,
    unselectPartnerProviderLevelSuggestion,
    unselectPartnerDeveloperLevelSuggestion,
    unselectPartnerAdvisorLevelSuggestion,
    unselectPartnerIntegratorSpecializationSuggestion,
    unselectPartnerProviderCiscoPoweredServiceSuggestion,
    unselectPartnerNameSuggestion,
    unselectSearchedTermsAsPartnerName,

    resetSelectedSuggestions,
    resetSelectedSuggestionFromSearchCriteria
  } = useSuggestions(
    initialSelectedCountry,
    initialSelectedPartnerIntegratorLevelSuggestions,
    initialSelectedPartnerProviderLevelSuggestions,
    initialSelectedPartnerDeveloperLevelSuggestions,
    initialSelectedPartnerAdvisorLevelSuggestions,
    initialSelectedPartnerIntegratorSpecializationSuggestions,
    initialSelectedPartnerProviderCiscoPoweredServiceSuggestions,
    initialSelectedPartnerNameSuggestion
  );

  const suggestionTypeSubTypeSelectFnMapping = {
    [`${appConstants.VALUE_LOCATION}-${appConstants.VALUE_COUNTRY}`]: selectCountryFilterValue,
    [`${appConstants.VALUE_PARTNER_NAME}-${appConstants.VALUE_PARTNER_NAME}`]: selectPartnerNameSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_INTEGRATOR}`]: selectPartnerIntegratorLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_PROVIDER}`]: selectPartnerProviderLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_DEVELOPER}`]: selectPartnerDeveloperLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_ADVISOR}`]: selectPartnerAdvisorLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_INTEGRATOR}`]: selectPartnerIntegratorSpecializationSuggestion,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_PROVIDER}`]: selectPartnerProviderCiscoPoweredServiceSuggestion,
    [`${appConstants.VALUE_PARTNER_NAME}-${appConstants.VALUE_PARTNER_NAME_KEYWORD}`]: selectSearchedTermsAsPartnerName
  };

  const suggestionTypeSubTypeUnselectFnMapping = {
    [`${appConstants.VALUE_LOCATION}-${appConstants.VALUE_COUNTRY}`]: unselectCountryFilterValue,
    [`${appConstants.VALUE_PARTNER_NAME}-${appConstants.VALUE_PARTNER_NAME}`]: unselectPartnerNameSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_INTEGRATOR}`]: unselectPartnerIntegratorLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_PROVIDER}`]: unselectPartnerProviderLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_DEVELOPER}`]: unselectPartnerDeveloperLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_LEVEL}-${appConstants.VALUE_ADVISOR}`]: unselectPartnerAdvisorLevelSuggestion,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_INTEGRATOR}`]: unselectPartnerIntegratorSpecializationSuggestion,
    [`${appConstants.VALUE_PARTNER_SPECIALIZATION}-${appConstants.VALUE_PROVIDER}`]: unselectPartnerProviderCiscoPoweredServiceSuggestion,
    [`${appConstants.VALUE_PARTNER_NAME}-${appConstants.VALUE_PARTNER_NAME_KEYWORD}`]: unselectSearchedTermsAsPartnerName
  };

  const { isSearchRestorationInProgress, restoreSearch } = useSearchRestoration();

  function onCountrySelectionChange() {
    syncPreviousAndCurrentlySelectedCountry();
    //if(!isSearchRestorationInProgress) {
    //  unselectPartnerNameSuggestion();
    //}
  }

  useOnCountrySelectionChange(previouslySelectedCountryCode, selectedCountryCode, onCountrySelectionChange);

  const {
    searchCriteria,
    derivedSelectedPartnerRoleCode
  } = useSearchCriteria(
    getCurrentSelectedForm(),
    countryFilterValues,
    partnerRoleFilterValues,
    partnerExpertiseFilterValues,
    partnerLevelFilterValues,
    selectedPartnerNameSuggestion,
    selectedPartnerIntegratorLevelSuggestions,
    selectedPartnerProviderLevelSuggestions,
    selectedPartnerDeveloperLevelSuggestions,
    selectedPartnerAdvisorLevelSuggestions,
    selectedPartnerIntegratorSpecializationSuggestions,
    selectedPartnerProviderCiscoPoweredServiceSuggestions,
    isSearchRestorationInProgress,
    isUserGeoLocationCompleted
  //selectedSearchTermsAsPartnerName,
  //unselectSearchedTermsAsPartnerName
  );

  const { recentSearches } = useRecentSearches(searchCriteria, getCurrentSelectedForm());

  const {
    isSortingParameterDropdownOpened,
    sortingParameterValues,
    selectSortingParameterValue,
    selectedSortingParameterValue,
    toggleSortingParameterDropdown
  } = useSortingParameter(initialSelectedSortingParameterValue);

  const {
    currentPageIndex,
    recordCountPerPage,
    previousPage,
    nextPage
  } = usePagination(initialCurrentPageIndex, appConstants.PARTNER_FOUND_RECORD_COUNT_PER_PAGE, searchCriteria);

  const [isSearchRequested, setIsSearchRequested] = useState(false);
  useEffect(
    () => {
      if(isUserGeoLocationCompleted) {
        setIsSearchRequested(true);
      }
    },
    [
    searchCriteria,
    currentPageIndex,
    appConstants.PARTNER_FOUND_RECORD_COUNT_PER_PAGE,
    selectedSortingParameterValue,
    isUserGeoLocationCompleted
  ]);
  const debouncedIsSearchRequested = useDebounce(isSearchRequested, 1000);

  const {
    foundPartners,
    setFoundPartners,
    foundPartnerCount,
    isPartnerSearchInProgress,
    partnerSearchProcessingError
  } = usePartnerSearch(
    searchCriteria,
    currentPageIndex,
    appConstants.PARTNER_FOUND_RECORD_COUNT_PER_PAGE,
    selectedSortingParameterValue,
    //isUserGeoLocationCompleted,
    debouncedIsSearchRequested,
    setIsSearchRequested
  );

  function updatePartnerSavedCount(partner, countChange) {
    setFoundPartners(previousValue => [...foundPartners.map((foundPartner) => { if(foundPartner[appConstants.KEY_SITE_ID_LOWERCASE] === partner[appConstants.KEY_SITE_ID_LOWERCASE]) { return { ...foundPartner, savedCount: foundPartner.savedCount + countChange }} else { return foundPartner }})]);
  }

  const {
    isItemBeingCompared,
    comparedItemList,
    comparedItemCategory,
    addItemToComparator,
    removeItemFromComparator,
    canAddToComparator,
    canActivateComparator,
    getComparedItemComparatorActionLabel,
    getNotComparedItemComparatorActionLabel,
    clearComparator
  } = useComparator();

  const {
    toggleFilters,
    showFilters,
    hideFilters,
    areFiltersShown
  } = useMobileFilterToggle();


  function navigateToPartnerProfilePage() {
    window.location.assign(`${window.location.href}partner/${initialSelectedPartnerSiteId}`);
  }

  useEffect(() => {
    if(!!initialSelectedPartnerSiteId) {
      navigateToPartnerProfilePage();
    }
  }, [initialSelectedPartnerSiteId]);

  function persistAppState(selectedPartnerSiteId = null) {
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_COUNTRY, selectedCountry);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_ROLE, selectedPartnerRole);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_LEVELS, selectedPartnerLevels);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_EXPERTISES, selectedPartnerExpertises);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_SEARCH_FORM, getCurrentSelectedForm());
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_LAST_SELECTED_PARTNER_SEARCH_FORM, lastSelectedNonSearchByLocationForm);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_PARTNER_SITE_ID, selectedPartnerSiteId);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SORTING_PARAMETER, selectedSortingParameterValue);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_CURRENT_PAGE_INDEX, currentPageIndex);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_INTEGRATOR_LEVELS, selectedPartnerIntegratorLevelSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_PROVIDER_LEVELS, selectedPartnerProviderLevelSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_DEVELOPER_LEVELS, selectedPartnerDeveloperLevelSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_ADVISOR_LEVELS, selectedPartnerAdvisorLevelSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_INTEGRATOR_SPECIALIZATIONS, selectedPartnerIntegratorSpecializationSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_PROVIDER_CPS, selectedPartnerProviderCiscoPoweredServiceSuggestions);
    setLocalStorageItem(appConstants.LOCALSTORAGE_KEY_PERSISTED_SELECTED_SUGGESTION_PARTNER_NAME, selectedPartnerNameSuggestion);
  }

  return (
    <PartnerSearchContext.Provider value={
      {
        isFilterLoadingInProgress,
        filterLoadingError,

        isCountryFilterDropdownOpened,
        countryFilterValues,
        selectedCountry,
        selectedCountryCode,
        selectedCountryName,
        selectCountryFilterValue,
        unselectCountryFilterValue,
        resetCountryFilterValues,
        toggleCountryFilterDropdown,

        isUserGeoLocationInProgress,

        getCurrentSelectedForm,
        selectFilterByNeedForm,
        selectSearchByCombinedCriteriaForm,
        selectSearchByLocationForm,
        selectFormBeforeSearchByLocation,
        isFilterByNeedFormSelected,
        isSearchByCombinedCriteriaFormSelected,
        isSearchByLocationFormSelected,

        partnerRoleFilterValues,
        isPartnerRoleFilterDropdownOpened,
        togglePartnerRoleFilterDropdown,
        selectPartnerRoleFilterValue,
        unselectPartnerRoleFilterValue,
        selectedPartnerRoleCode,

        partnerLevelFilterValues,
        isPartnerLevelFilterDropdownOpened,
        togglePartnerLevelFilterDropdown,
        togglePartnerLevelFilterValue,
        resetPartnerLevelFilterValues,

        partnerExpertiseFilterValues,
        isPartnerExpertiseFilterDropdownOpened,
        togglePartnerExpertiseFilterDropdown,
        togglePartnerExpertiseFilterValue,
        resetPartnerExpertiseFilterValues,

        //filterTypeSubTypeSelectFnMapping,
        filterTypeSubTypeUnselectFnMapping,

        searchTerms,
        updateSearchTerms,
        clearSearchTerms,
        debouncedSearchTerms,


        isGetSuggestionsProcessingInProgress,
        getSuggestionsProcessingError,
        partnerIntegratorLevelSuggestions,
        partnerProviderLevelSuggestions,
        partnerDeveloperLevelSuggestions,
        partnerAdvisorLevelSuggestions,
        partnerIntegratorSpecializationSuggestions,
        partnerProviderCiscoPoweredServiceSuggestions,
        partnerNameSuggestions,
        searchedTermsAsPartnerNameSuggestion,

        selectedPartnerIntegratorLevelSuggestions,
        selectedPartnerProviderLevelSuggestions,
        selectedPartnerDeveloperLevelSuggestions,
        selectedPartnerAdvisorLevelSuggestions,
        selectedPartnerIntegratorSpecializationSuggestions,
        selectedPartnerProviderCiscoPoweredServiceSuggestions,
        selectedPartnerNameSuggestion,
        selectedSearchTermsAsPartnerName,

        suggestionTypeSubTypeSelectFnMapping,
        suggestionTypeSubTypeUnselectFnMapping,

        resetSelectedSuggestions,
        resetSelectedSuggestionFromSearchCriteria,

        isSortingParameterDropdownOpened,
        sortingParameterValues,
        selectSortingParameterValue,
        selectedSortingParameterValue,
        toggleSortingParameterDropdown,

        searchCriteria,
        derivedSelectedPartnerRoleCode,

        recentSearches,
        isSearchRestorationInProgress,
        restoreSearch,

        foundPartners,
        updatePartnerSavedCount,
        foundPartnerCount,
        isPartnerSearchInProgress,
        partnerSearchProcessingError,

        currentPageIndex,
        recordCountPerPage,
        previousPage,
        nextPage,

        isItemBeingCompared,
        comparedItemList,
        comparedItemCategory,
        addItemToComparator,
        removeItemFromComparator,
        canAddToComparator,
        canActivateComparator,
        getComparedItemComparatorActionLabel,
        getNotComparedItemComparatorActionLabel,
        clearComparator,

        toggleFilters,
        showFilters,
        hideFilters,
        areFiltersShown,

        persistAppState
      }
    }>
      {children}
    </PartnerSearchContext.Provider>
  );
}
